import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaPhoneAlt, FaWhatsapp } from "react-icons/fa";
import {
  AddLeadMessageSlice,
  UpdateLeadStatusSlice,
} from "../../redux/features/LeadManagementSlice";
import LeadModal from "./components/LeadModal.jsx";
import StatusBadge from "./components/StatusBadge.jsx";
import {
  LEAD_STATUSES,
  formatDate,
  formatTime,
  getInitials,
  getStatusMeta,
  getWhatsAppUrl,
  isPastDate,
  isToday,
  todayInputValue,
} from "./leadConstants";

const InfoItem = ({ label, value, full = false, highlight }) => (
  <div className={`lm-info-item ${full ? "lm-info-item--full" : ""}`}>
    <span className="lm-info-label">{label}</span>
    <span className={`lm-info-value ${highlight ? `lm-text-${highlight}` : ""}`}>
      {value || "—"}
    </span>
  </div>
);

const getFollowupHighlight = (date) => {
  if (isToday(date)) return "warning";
  if (isPastDate(date)) return "danger";
  return undefined;
};

const LeadDetailModal = ({ lead, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { detailLoading, statusUpdating, messageSubmitting } = useSelector(
    (state) => state.leadManagement
  );

  const [statusValue, setStatusValue] = useState("");
  const [messageText, setMessageText] = useState("");
  const [followupDate, setFollowupDate] = useState("");

  const leadId = lead?._id;

  // Reset the note form whenever a different lead is opened
  useEffect(() => {
    setMessageText("");
    setFollowupDate("");
  }, [leadId]);

  useEffect(() => {
    setStatusValue(lead?.status || "");
  }, [leadId, lead?.status]);

  if (!lead) return null;

  const currentStatus = getStatusMeta(lead.status);
  const services = Array.isArray(lead.services) ? lead.services : [];
  const messages = Array.isArray(lead.messages) ? [...lead.messages].reverse() : [];
  const whatsappUrl = getWhatsAppUrl(lead.phoneno, lead.name);

  const handleStatusUpdate = () => {
    if (!statusValue || statusValue === lead.status) return;
    dispatch(UpdateLeadStatusSlice({ id: lead._id, data: { status: statusValue } }));
  };

  const handleAddMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) {
      toast.error("Message text is required");
      return;
    }

    const data = { message: messageText.trim() };
    if (followupDate) data.followupDate = new Date(followupDate).toISOString();

    try {
      await dispatch(AddLeadMessageSlice({ id: lead._id, data })).unwrap();
      setMessageText("");
      setFollowupDate("");
    } catch {
      // Error toast is handled in the slice
    }
  };

  const title = (
    <div className="lm-detail-title">
      <div className="lm-avatar lm-avatar--lg" style={{ "--lm-status": currentStatus.color, "--lm-status-bg": currentStatus.bg }}>
        {getInitials(lead.name)}
      </div>
      <div>
        <h4>{lead.name || "Unnamed Lead"}</h4>
        <div className="lm-detail-meta">
          <StatusBadge status={lead.status} />
          <span className="lm-muted">Created {formatDate(lead.createdAt)}, {formatTime(lead.createdAt)}</span>
        </div>
      </div>
    </div>
  );

  const headerExtra = (
    <div className="lm-contact-actions">
      <a href={`tel:${lead.phoneno}`} className="lm-btn lm-btn--outline lm-btn--sm">
        <FaPhoneAlt size={11} /> Call
      </a>
      {whatsappUrl && (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="lm-btn lm-btn--whatsapp lm-btn--sm"
        >
          <FaWhatsapp size={14} /> WhatsApp
        </a>
      )}
    </div>
  );

  return (
    <LeadModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      headerExtra={headerExtra}
      accentColor={currentStatus.color}
      size="xl"
    >
      {detailLoading && <div className="lm-progress" aria-label="Refreshing lead" />}

      {/* ---------- Lead information ---------- */}
      <section className="lm-card">
        <h6 className="lm-section-title">Lead Information</h6>
        <div className="lm-info-grid">
          <InfoItem label="Phone Number" value={lead.phoneno && `+91 ${lead.phoneno}`} />
          <InfoItem label="Event Category" value={lead.categoryName} />
          <InfoItem label="Decoration / Package" value={lead.decorationName} />
          <InfoItem label="Event Date" value={lead.eventDate && formatDate(lead.eventDate)} />
          <InfoItem label="Event Time" value={lead.eventTime} />
          <InfoItem label="Plan Type" value={lead.planType} />
          <InfoItem
            label="Next Follow-up"
            value={lead.nextFollowupDate && formatDate(lead.nextFollowupDate)}
            highlight={getFollowupHighlight(lead.nextFollowupDate)}
          />
          <InfoItem label="Last Updated" value={lead.updatedAt && `${formatDate(lead.updatedAt)}, ${formatTime(lead.updatedAt)}`} />
          <InfoItem label="Total Follow-ups" value={String(lead.messages?.length ?? 0)} />
          <InfoItem label="Address" value={lead.address} full />
        </div>

        <div className="lm-info-services">
          <span className="lm-info-label">Services Required</span>
          {services.length > 0 ? (
            <div className="lm-chip-list">
              {services.map((service) => (
                <span key={service} className="lm-chip">{service}</span>
              ))}
            </div>
          ) : (
            <span className="lm-info-value">—</span>
          )}
        </div>
      </section>

      {/* ---------- Status ---------- */}
      <section className="lm-card">
        <div className="lm-card-header">
          <h6 className="lm-section-title mb-0">Lead Status</h6>
          <button
            type="button"
            className="lm-btn lm-btn--primary lm-btn--sm"
            onClick={handleStatusUpdate}
            disabled={statusUpdating || !statusValue || statusValue === lead.status}
          >
            {statusUpdating ? <><span className="lm-spinner" /> Updating...</> : "Update Status"}
          </button>
        </div>

        <div className="lm-status-picker" role="radiogroup" aria-label="Lead status">
          {LEAD_STATUSES.map((status) => {
            const selected = statusValue === status.value;
            return (
              <button
                key={status.value}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`lm-status-option ${selected ? "is-selected" : ""}`}
                style={{ "--lm-status": status.color, "--lm-status-bg": status.bg }}
                onClick={() => setStatusValue(status.value)}
              >
                <span className="lm-status-dot" />
                {status.label}
                {status.value === lead.status && <span className="lm-status-current">Current</span>}
              </button>
            );
          })}
        </div>
      </section>

      {/* ---------- Follow-ups ---------- */}
      <div className="lm-followup-layout">
        <section className="lm-card">
          <h6 className="lm-section-title">Add Follow-up Note</h6>
          <form onSubmit={handleAddMessage} className="lm-followup-form">
            <div className="lm-field">
              <label htmlFor="lead-message">Note <span className="lm-required">*</span></label>
              <textarea
                id="lead-message"
                className="lm-input"
                rows={4}
                placeholder="What did the customer say? Next steps..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
            </div>
            <div className="lm-field">
              <label htmlFor="lead-followup-date">Next Follow-up Date</label>
              <input
                id="lead-followup-date"
                type="date"
                className="lm-input"
                min={todayInputValue()}
                value={followupDate}
                onChange={(e) => setFollowupDate(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="lm-btn lm-btn--primary lm-btn--block"
              disabled={messageSubmitting || !messageText.trim()}
            >
              {messageSubmitting ? <><span className="lm-spinner" /> Saving...</> : "Add Note"}
            </button>
          </form>
        </section>

        <section className="lm-card">
          <h6 className="lm-section-title">
            Follow-up History <span className="lm-count">{messages.length}</span>
          </h6>

          {messages.length > 0 ? (
            <ul className="lm-timeline">
              {messages.map((msg) => (
                <li key={msg._id} className="lm-timeline-item">
                  <div className="lm-timeline-head">
                    <strong>{msg?.salesPerson?.name || "Team"}</strong>
                    <span className="lm-muted">{formatDate(msg?.createdAt)}, {formatTime(msg?.createdAt)}</span>
                  </div>
                  <p>{msg?.message}</p>
                  {msg?.followupDate && (
                    <span className={`lm-timeline-tag lm-text-${getFollowupHighlight(msg.followupDate) || "info"}`}>
                      Follow-up: {formatDate(msg.followupDate)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="lm-empty-mini">No follow-ups yet. Add the first note.</div>
          )}
        </section>
      </div>
    </LeadModal>
  );
};

export default LeadDetailModal;
